import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { TimeElapsed, CoupleSettings } from '../types';
import { Calendar, Heart } from 'lucide-react';
import { romanticAudio } from '../utils/audio';

interface CounterHUDProps {
  timeElapsed: TimeElapsed;
  settings: CoupleSettings;
  isAdmin?: boolean;
  onOpenSettings?: () => void;
}

interface FloatingHeart {
  id: number;
  x: number;
  y: number;
  char: string;
}

export const CounterHUD: React.FC<CounterHUDProps> = React.memo(({
  timeElapsed,
  settings,
  isAdmin = false,
  onOpenSettings,
}) => {
  const [floatingHearts, setFloatingHearts] = useState<FloatingHeart[]>([]);
  const [showCelebrationBadge, setShowCelebrationBadge] = useState(false);

  // 6 counter cards matching the requested layout
  const counterUnits = [
    { label: 'AÑOS', value: timeElapsed.years, isSeconds: false },
    { label: 'MESES', value: timeElapsed.months, isSeconds: false },
    { label: 'DÍAS', value: timeElapsed.days, isSeconds: false },
    { label: 'HORAS', value: timeElapsed.hours, isSeconds: false },
    { label: 'MIN', value: timeElapsed.minutes, isSeconds: false },
    { label: 'SEG', value: timeElapsed.seconds, isSeconds: true },
  ];

  // Interactive celebration on tap/click
  const handleHUDClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    try {
      romanticAudio.playChime();
    } catch {}

    const chars = ['💖', '✨', '🌸', '💕', '⭐'];
    const newHearts: FloatingHeart[] = Array.from({ length: 4 }).map((_, i) => ({
      id: Date.now() + Math.random() + i,
      x: x + (Math.random() - 0.5) * 40,
      y: y + (Math.random() - 0.5) * 20,
      char: chars[Math.floor(Math.random() * chars.length)],
    }));

    setFloatingHearts((prev) => [...prev.slice(-8), ...newHearts]);
    setShowCelebrationBadge(true);

    setTimeout(() => {
      setFloatingHearts((prev) => prev.filter((h) => !newHearts.some((nh) => nh.id === h.id)));
    }, 1200);

    setTimeout(() => {
      setShowCelebrationBadge(false);
    }, 2800);
  }, []);

  // Format quote: fallback to the exact quote from user's reference image
  const displayQuote = settings.quote && settings.quote.trim()
    ? settings.quote.trim()
    : 'Cada segundo a tu lado florece como la primera vez';

  return (
    <div className="w-full max-w-full flex flex-col items-center justify-center px-2 xs:px-3 sm:px-4 select-none">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 14 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
        onClick={handleHUDClick}
        className="relative w-full max-w-[540px] sm:max-w-xl mx-auto rounded-3xl bg-white/95 backdrop-blur-xl border border-[#F0DDE4] shadow-[0_8px_30px_rgba(232,93,142,0.12)] p-2.5 xs:p-3 sm:p-4 cursor-pointer group transition-all"
        id="relationship-counter-hud"
        title="Nuestros días juntos — Toca para celebrar nuestro amor"
      >
        {/* Top subtle pink sheen */}
        <div className="absolute top-0 inset-x-8 h-[1px] bg-gradient-to-r from-transparent via-[#E85D8E]/30 to-transparent pointer-events-none" />

        {/* Floating Celebration Hearts */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl z-40">
          <AnimatePresence>
            {floatingHearts.map((h) => (
              <motion.span
                key={h.id}
                initial={{ opacity: 1, scale: 0.6, x: h.x, y: h.y }}
                animate={{
                  opacity: 0,
                  scale: 1.4,
                  y: h.y - 70,
                  x: h.x + (Math.random() - 0.5) * 30,
                }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.1, ease: 'easeOut' }}
                className="absolute text-base sm:text-lg select-none pointer-events-none"
              >
                {h.char}
              </motion.span>
            ))}
          </AnimatePresence>
        </div>

        {/* Floating Badge on click showing total days */}
        <AnimatePresence>
          {showCelebrationBadge && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.9 }}
              animate={{ opacity: 1, y: -28, scale: 1 }}
              exit={{ opacity: 0, y: -34, scale: 0.9 }}
              transition={{ duration: 0.28 }}
              className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-[#E85D8E] to-[#E83E78] text-white rounded-full text-xs font-bold shadow-md shadow-pink-500/30 border border-white flex items-center gap-1.5 whitespace-nowrap z-50 pointer-events-none"
            >
              <Heart className="w-3.5 h-3.5 fill-white" />
              <span>¡{timeElapsed.totalDays || 0} días de amor verdadero!</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Admin settings indicator */}
        {isAdmin && onOpenSettings && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenSettings();
            }}
            title="Ajustes de Fecha y Frase"
            className="absolute -top-2.5 -right-2.5 p-1.5 sm:p-2 bg-white text-[#E83E78] hover:bg-[#FFF4F7] rounded-full shadow-md hover:scale-110 active:scale-95 border border-[#F0DDE4] cursor-pointer z-30 transition-all"
            id="btn-hud-settings"
          >
            <Calendar className="w-3.5 h-3.5" />
          </button>
        )}

        {/* 1. ROW OF 6 ROUNDED PILL CARDS (AÑOS, MESES, DÍAS, HORAS, MIN, SEG) */}
        <div className="grid grid-cols-6 gap-1 xs:gap-1.5 sm:gap-2.5 w-full relative z-10">
          {counterUnits.map((item) => (
            <div
              key={item.label}
              className={`relative flex flex-col items-center justify-center py-2 sm:py-3.5 px-0.5 sm:px-2 rounded-2xl sm:rounded-[1.25rem] transition-all duration-200 ${
                item.isSeconds
                  ? 'bg-[#FFF4F7] border-2 border-[#E85D8E] shadow-[0_2px_10px_rgba(232,93,142,0.18)]'
                  : 'bg-[#F8F5F7] border border-[#F0DDE4] shadow-xs'
              }`}
            >
              {/* Number Value */}
              <div className="flex items-center justify-center min-h-[1.75rem] xs:min-h-[2rem] sm:min-h-[2.5rem]">
                {item.isSeconds ? (
                  <motion.span
                    key={item.value}
                    initial={{ scale: 0.94, opacity: 0.8 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                    className="text-lg xs:text-xl sm:text-2xl md:text-3xl font-extrabold tracking-tight text-[#E83E78] select-none font-sans"
                  >
                    {item.value}
                  </motion.span>
                ) : (
                  <span className="text-lg xs:text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-[#302B30] select-none font-sans">
                    {item.value}
                  </span>
                )}
              </div>

              {/* Unit Label */}
              <span className={`text-[8px] xs:text-[9.5px] sm:text-[11px] font-bold tracking-wider uppercase mt-0.5 sm:mt-1 whitespace-nowrap select-none ${
                item.isSeconds ? 'text-[#E83E78]' : 'text-[#77717A]'
              }`}>
                {item.label}
              </span>
            </div>
          ))}
        </div>

        {/* 2. SUBTLE HORIZONTAL DIVIDER LINE */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-[#F0DDE4] to-transparent my-2 sm:my-3 pointer-events-none" />

        {/* 3. ROMANTIC QUOTE WITH DECORATIVE GOLDEN QUOTES */}
        <div className="w-full flex items-center justify-center gap-1.5 xs:gap-2 px-1 text-center">
          {/* Left golden quote mark */}
          <span
            className="text-[#E83E78] font-serif font-black text-sm xs:text-base sm:text-xl select-none shrink-0 leading-none"
            aria-hidden="true"
          >
            ❝
          </span>

          {/* Quote text */}
          <p className="font-semibold text-xs xs:text-sm sm:text-base text-[#302B30] tracking-normal leading-snug truncate max-w-[85%] font-['Playfair_Display',serif]">
            &ldquo;{displayQuote}&rdquo;
          </p>

          {/* Right golden quote mark */}
          <span
            className="text-[#E83E78] font-serif font-black text-sm xs:text-base sm:text-xl select-none shrink-0 leading-none"
            aria-hidden="true"
          >
            ❞
          </span>
        </div>
      </motion.div>
    </div>
  );
});

CounterHUD.displayName = 'CounterHUD';
